import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveHmsAsSafetyRep,
  approveHmsRiskMatrix,
  approveHmsTemplateVersion,
  discardHmsTemplateDraft,
  getHmsRiskMatrix,
  getRiskMatrixSetup,
  getHmsSafetyRepOverview,
  getHmsSafetyRepRiskMatrixVersion,
  getHmsSafetyRepTemplateVersion,
  getHmsTemplate,
  getHmsTemplateVersion,
  type HmsApprovalInput,
  type HmsSubject,
  returnHmsAsSafetyRep,
  type SafetyRepOverview,
  type RiskMatrixOverview,
  saveHmsTemplateDraft,
  saveHmsTemplateLanguages,
  saveHmsTranslations,
  suggestHmsTranslations,
  type TemplateLanguage,
  type TranslationEntry,
  setHmsTemplateAvailability,
  submitHmsRiskMatrix,
  submitHmsTemplateDraft,
  type SubmitRiskMatrixRequest,
  type TemplateFactorInput,
  type TemplateOverview,
  withdrawHmsRiskMatrix,
  withdrawHmsTemplateVersion,
} from '../../../api/hms';
import { SafetyRepWorkQueryKey } from '../safetyRep/useSafetyRepWork';
import { USER_ONBOARDING_KEY } from '../onboarding/useUserOnboarding';

export const hmsKeys = {
  riskMatrix: ['hms', 'risk-matrix'] as const,
  riskMatrixSetup: ['hms', 'risk-matrix-setup'] as const,
  template: ['hms', 'template'] as const,
  templateVersion: (id: number) => ['hms', 'template', 'version', id] as const,
  safetyRep: ['hms', 'safety-rep'] as const,
  safetyRepVersion: (subject: HmsSubject, id: number) => ['hms', 'safety-rep', subject, id] as const,
};

export const useHmsRiskMatrixQuery = (enabled = true) =>
  useQuery({ queryKey: hmsKeys.riskMatrix, queryFn: getHmsRiskMatrix, enabled });

/** Whether the risk matrix has ever been changed from the standard, and the routine links still missing */
export const useRiskMatrixSetupQuery = (enabled = true) =>
  useQuery({ queryKey: hmsKeys.riskMatrixSetup, queryFn: getRiskMatrixSetup, enabled });

export const useHmsTemplateQuery = (enabled = true) =>
  useQuery({ queryKey: hmsKeys.template, queryFn: getHmsTemplate, enabled });

export const useHmsTemplateVersionQuery = (id: number | null) =>
  useQuery({
    queryKey: hmsKeys.templateVersion(id ?? 0),
    queryFn: () => getHmsTemplateVersion(id as number),
    enabled: id !== null,
  });

/** Every change responds with the new overview, so the page updates without refetching */
const useRiskMatrixMutation = <T>(mutationFn: (input: T) => Promise<RiskMatrixOverview>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (overview) => {
      queryClient.setQueryData(hmsKeys.riskMatrix, overview);
      // Risikomatrisen viser faktorene fra malen
      queryClient.invalidateQueries({ queryKey: hmsKeys.template });
      // A saved version ticks off the risk matrix in «Kom i gang»
      queryClient.invalidateQueries({ queryKey: USER_ONBOARDING_KEY });
      // ...and means the matrix is no longer the standard
      queryClient.invalidateQueries({ queryKey: hmsKeys.riskMatrixSetup });
    },
  });
};

const useTemplateMutation = <T>(mutationFn: (input: T) => Promise<TemplateOverview>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (overview) => {
      queryClient.setQueryData(hmsKeys.template, overview);
      queryClient.invalidateQueries({
        queryKey: [...hmsKeys.template, 'version'],
      });
      queryClient.invalidateQueries({ queryKey: hmsKeys.riskMatrix });
    },
  });
};

export const useSubmitHmsRiskMatrixMutation = () =>
  useRiskMatrixMutation((request: SubmitRiskMatrixRequest) => submitHmsRiskMatrix(request));
export const useApproveHmsRiskMatrixMutation = () =>
  useRiskMatrixMutation(({ id, comment }: { id: number; comment: string }) => approveHmsRiskMatrix(id, comment));
export const useWithdrawHmsRiskMatrixMutation = () => useRiskMatrixMutation((id: number) => withdrawHmsRiskMatrix(id));

export const useSaveHmsTemplateDraftMutation = () =>
  useTemplateMutation((factors: TemplateFactorInput[]) => saveHmsTemplateDraft(factors));
export const useSaveHmsTemplateLanguagesMutation = () =>
  useTemplateMutation((languages: TemplateLanguage[]) => saveHmsTemplateLanguages(languages));
export const useSaveHmsTranslationsMutation = () =>
  useTemplateMutation(({ language, entries }: { language: string; entries: TranslationEntry[] }) =>
    saveHmsTranslations(language, entries),
  );
export const useSuggestHmsTranslationsMutation = () =>
  useTemplateMutation((language: string) => suggestHmsTranslations(language));
export const useDiscardHmsTemplateDraftMutation = () => useTemplateMutation(() => discardHmsTemplateDraft());
export const useSubmitHmsTemplateDraftMutation = () =>
  useTemplateMutation((approval: HmsApprovalInput) => submitHmsTemplateDraft(approval));
export const useApproveHmsTemplateVersionMutation = () =>
  useTemplateMutation(({ id, comment }: { id: number; comment: string }) => approveHmsTemplateVersion(id, comment));
export const useWithdrawHmsTemplateVersionMutation = () =>
  useTemplateMutation((id: number) => withdrawHmsTemplateVersion(id));
export const useSetHmsTemplateAvailabilityMutation = () =>
  useTemplateMutation(({ id, availableForNew }: { id: number; availableForNew: boolean }) =>
    setHmsTemplateAvailability(id, availableForNew),
  );

export const useHmsSafetyRepQuery = () => useQuery({ queryKey: hmsKeys.safetyRep, queryFn: getHmsSafetyRepOverview });

export const useHmsSafetyRepRiskMatrixVersionQuery = (id: number) =>
  useQuery({
    queryKey: hmsKeys.safetyRepVersion('riskMatrix', id),
    queryFn: () => getHmsSafetyRepRiskMatrixVersion(id),
  });

export const useHmsSafetyRepTemplateVersionQuery = (id: number) =>
  useQuery({
    queryKey: hmsKeys.safetyRepVersion('template', id),
    queryFn: () => getHmsSafetyRepTemplateVersion(id),
  });

/**
 * The safety representative's decision changes what HR sees, so the HR overviews are refetched, and their own tasks,
 * so the task leaves «Mine oppgaver» and the count in the menu
 */
const useSafetyRepMutation = <T>(mutationFn: (input: T) => Promise<SafetyRepOverview>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (overview) => {
      queryClient.setQueryData(hmsKeys.safetyRep, overview);
      queryClient.invalidateQueries({ queryKey: hmsKeys.riskMatrix });
      queryClient.invalidateQueries({ queryKey: hmsKeys.template });
      queryClient.invalidateQueries({ queryKey: [SafetyRepWorkQueryKey] });
    },
  });
};

export const useApproveHmsAsSafetyRepMutation = () =>
  useSafetyRepMutation(({ subject, id }: { subject: HmsSubject; id: number }) => approveHmsAsSafetyRep(subject, id));
export const useReturnHmsAsSafetyRepMutation = () =>
  useSafetyRepMutation(({ subject, id, comment }: { subject: HmsSubject; id: number; comment: string }) =>
    returnHmsAsSafetyRep(subject, id, comment),
  );
